import classNames from 'classnames'

import ItemTableBodyRow from 'components/Core/Table/ItemTableBodyRow'
import ItemTableExpandableBodyRow from 'components/Core/Table/ItemTableExpandableBodyRow'

import styles from './SummaryTable.module.css'
import {
  ColumnOrder,
  OrderData,
  RowData,
  TableColumnData,
} from './SummaryTable.types'

export const LARGE_WIDTH_CELL = 'large'
export const MEDIUM_WIDTH_CELL = 'medium'
export const SMALL_WIDTH_CELL = 'small'

export const summaryTableColumns: TableColumnData = {
  orderName: {
    description: 'Order',
    width: LARGE_WIDTH_CELL,
    cellContent: (allocation: OrderData): string =>
      formatTextValueForTable(allocation?.orderName ?? ''),
  },
  skus: {
    description: 'SKUs',
    width: SMALL_WIDTH_CELL,
    cellContent: (allocation: OrderData): string =>
      formatNumberValueForTable(allocation.skus),
  },
  units: {
    description: 'Units',
    width: SMALL_WIDTH_CELL,
    cellContent: (allocation: OrderData): string =>
      formatNumberValueForTable(allocation.units),
  },
  total: {
    description: `Total`,
    width: LARGE_WIDTH_CELL,
    cellContent: (allocation: OrderData): string =>
      formatCurrencyValueForTable(allocation.total),
  },
}

export const summaryTableColumnOrder: ColumnOrder = [
  'orderName',
  'skus',
  'units',
  'total',
]

export const formatTextValueForTable = (
  value: string | number | undefined,
): string => (typeof value === 'number' ? value.toString() : value) || ' '

export const formatNumberValueForTable = (value: number): string =>
  (isFinite(value) && value.toString()) || ' '

export const formatCurrencyValueForTable = (
  value: number,
  currency = '',
): string =>
  `$ ${
    value === 0
      ? value
      : value.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
  }${currency || ''}`

export const renderExpandableRow = (
  item: OrderData,
  rowData: RowData,
  onExpandAnyRow?: (anyOpen: boolean) => void,
): JSX.Element => {
  const { columnOrder, columns, rowsExpanded } = rowData
  const rowClassnames = {
    expandableRow: classNames(styles.expandableRow, {
      [styles.hasPointer]: !!item?.subItems?.length,
    }),
    expandedRow: styles.expandedRow,
  }
  return (
    // @ts-expect-error
    <ItemTableExpandableBodyRow
      key={item.id}
      columnOrder={columnOrder}
      columns={columns}
      item={item}
      rowClassnames={rowClassnames}
      rowsExpanded={rowsExpanded}
      aggregationKeys={['orderName', 'skus', 'units', 'total']}
      onExpandAnyRow={onExpandAnyRow}
    />
  )
}

export const renderSingleRow = (
  item: OrderData,
  rowData: RowData,
): JSX.Element => {
  const { columnOrder, columns, isExpandedRow } = rowData
  return (
    // @ts-expect-error
    <ItemTableBodyRow
      key={item.id}
      className={styles.summaryRow}
      selectable={false}
      columnOrder={columnOrder}
      columns={columns}
      item={item}
      isExpandedRow={isExpandedRow}
    />
  )
}

export const renderGeneralRow = (
  item: OrderData | null,
  rowData: RowData,
): JSX.Element | null => {
  const { columns, columnOrder, isExpandedRow } = rowData
  return item
    ? renderSingleRow(item, {
        columns,
        columnOrder,
        isExpandedRow,
      })
    : null
}

export const renderRows = (
  items: OrderData[],
  rowData: RowData,
): JSX.Element[] => {
  const { columns, columnOrder, rowsExpanded, isExpandedRow } = rowData
  return items.map((item) =>
    !!item?.subItems?.length
      ? renderExpandableRow(item, { columns, columnOrder, rowsExpanded })
      : renderSingleRow(item, {
          columns,
          columnOrder,
          isExpandedRow,
        }),
  )
}

export const renderExpandableRows = (
  items: OrderData[],
  rowData: RowData,
  onExpandAnyRow: (anyOpen: boolean) => void,
): JSX.Element[] => {
  const { columns, columnOrder, rowsExpanded } = rowData
  return items.map((item) =>
    renderExpandableRow(
      item,
      { columns, columnOrder, rowsExpanded },
      onExpandAnyRow,
    ),
  )
}
